import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, KeyRound, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { CredentialsDialog } from "@/components/CredentialsDialog.tsx";
import { ApiError, api } from "@/lib/api.ts";
import {
  type NodeCredentialsOut,
  type NodeOut,
  STATUS_LABELS,
  STATUS_TONES,
  type TenantOut,
  type TenantStatus,
  ago,
  formatPEN,
} from "@/lib/types.ts";

export function TenantPage() {
  const { tenantId = "" } = useParams();
  const qc = useQueryClient();
  // /tenants trae el panorama de todas; se reutiliza la misma consulta del tablero.
  const tenants = useQuery({ queryKey: ["tenants"], queryFn: () => api.get<TenantOut[]>("/tenants") });
  const nodes = useQuery({
    queryKey: ["nodes", tenantId],
    queryFn: () => api.get<NodeOut[]>(`/tenants/${tenantId}/nodes`),
  });
  const [credentials, setCredentials] = useState<NodeCredentialsOut | null>(null);
  const [nodeName, setNodeName] = useState("");

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["tenants"] });
    qc.invalidateQueries({ queryKey: ["metrics"] });
    qc.invalidateQueries({ queryKey: ["nodes", tenantId] });
  };
  const updateTenant = useMutation({
    mutationFn: (body: Partial<Pick<TenantOut, "status" | "plan">>) =>
      api.patch<TenantOut>(`/tenants/${tenantId}`, body),
    onSuccess: refresh,
  });
  const provision = useMutation({
    mutationFn: () => api.post<NodeCredentialsOut>(`/tenants/${tenantId}/nodes`, { name: nodeName }),
    onSuccess: (out) => {
      setCredentials(out);
      setNodeName("");
      refresh();
    },
  });
  const rotate = useMutation({
    mutationFn: (nodeId: string) => api.post<NodeCredentialsOut>(`/nodes/${nodeId}/rotate`),
    onSuccess: (out) => {
      setCredentials(out);
      refresh();
    },
  });
  const toggleNode = useMutation({
    mutationFn: (node: NodeOut) => api.patch<NodeOut>(`/nodes/${node.id}`, { is_active: !node.is_active }),
    onSuccess: refresh,
  });

  const tenant = tenants.data?.find((t) => t.id === tenantId);
  if (tenants.isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;
  if (!tenant) return <p className="text-sm text-red-700">Empresa no encontrada.</p>;

  const error = [updateTenant.error, provision.error, rotate.error, toggleNode.error].find(Boolean);

  return (
    <div className="space-y-6">
      <Link to="/" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Empresas
      </Link>

      <section className="rounded-[1.375rem] border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{tenant.legal_name}</h2>
            <p className="text-sm text-slate-500">
              RUC {tenant.ruc}
              {tenant.trade_name && ` · ${tenant.trade_name}`} · alta {new Date(tenant.created_at).toLocaleDateString("es-PE")}
            </p>
            <p className="mt-2 text-sm text-slate-600">
              {tenant.branches} sucursal(es) · {tenant.active_users} usuarios activos · hoy{" "}
              {formatPEN(tenant.sales_today_total)} ({tenant.sales_today} ventas) · última venta{" "}
              {ago(tenant.last_sale_at)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TONES[tenant.status]}`}>
              {STATUS_LABELS[tenant.status]}
            </span>
            <select
              value={tenant.status}
              onChange={(e) => updateTenant.mutate({ status: e.target.value as TenantStatus })}
              aria-label="Cambiar estado"
              className="rounded-xl border border-slate-300 px-2 py-1 text-sm"
            >
              {(Object.keys(STATUS_LABELS) as TenantStatus[]).map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(tenant.status === "suspended" || tenant.status === "cancelled") && (
          <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
            Sin acceso al POS en la nube y sin sincronización de sus servidores locales (que siguen
            vendiendo en local).
          </p>
        )}
      </section>

      <section className="rounded-[1.375rem] border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-3">
          <h3 className="font-semibold text-slate-900">Servidores locales (edición offline)</h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              provision.mutate();
            }}
            className="flex gap-2"
          >
            <input
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              placeholder="Nombre (p. ej. Sucursal Centro)"
              required
              className="rounded-xl border border-slate-300 px-3 py-1.5 text-sm"
            />
            <button
              type="submit"
              disabled={provision.isPending}
              className="flex items-center gap-1 rounded-xl bg-brand-600 px-4 py-2 text-sm font-bold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" /> Registrar
            </button>
          </form>
        </div>
        <p className="border-b border-slate-100 px-4 py-2 text-xs text-slate-500">
          Registrado sin sucursal, el servidor opera todas las de la empresa. Para uno por sucursal,
          usa <span className="font-mono">scripts/provision_node.py --branch-id</span> hasta que el
          alta guiada lo incluya.
        </p>
        {nodes.data && nodes.data.length === 0 && (
          <p className="p-6 text-center text-sm text-slate-500">Vende solo en la nube.</p>
        )}
        {nodes.data && nodes.data.length > 0 && (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-slate-100">
              {nodes.data.map((n) => (
                <tr key={n.id}>
                  <td className="px-4 py-2">
                    <p className="font-medium text-slate-900">{n.name}</p>
                    <p className="font-mono text-[11px] text-slate-400">{n.id}</p>
                  </td>
                  <td className="px-4 py-2 text-slate-600">contacto {ago(n.last_seen_at)}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">secreto v{n.secret_version}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => rotate.mutate(n.id)}
                        title="Genera un secreto nuevo; el anterior deja de servir"
                        className="flex items-center gap-1 rounded-xl border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        <KeyRound className="h-3.5 w-3.5" /> Rotar secreto
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleNode.mutate(n)}
                        className="rounded-xl border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        {n.is_active ? "Desactivar" : "Activar"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {error && (
        <p className="text-sm text-red-700">
          {error instanceof ApiError ? error.message : "Operación fallida"}
        </p>
      )}
      {credentials && (
        <CredentialsDialog
          title={`Credenciales de ${credentials.node.name}`}
          env={credentials.env}
          onClose={() => setCredentials(null)}
        />
      )}
    </div>
  );
}
